import { inject, ref } from 'vue'
import { RequestService } from '../services/RequestService'
import { AgencyDataService } from '../services/AgencyDataService'
import { defineStore } from 'pinia'
import { find, get } from 'lodash'
import { getApp } from 'firebase/app';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

import { useAuthStore } from './AuthStore'
import { canAccessCapability, API_CAPABILITY } from '../access/apiCapabilities'

const { post, put, simpleGet, handleErrors } = RequestService()

function canCallCoreWorkbenchApi() {
  const auth = useAuthStore()
  if (!auth.accessLoaded) {
    return false
  }
  return canAccessCapability(auth, API_CAPABILITY.CORE_WORKBENCH)
}

export const useEmployerStore = defineStore(
  'employer',
  () => {
    const actualEmployer = ref(null)
    const planYearsOfActualEmployer = ref(null)
    const currentPlanYearCopy = ref(null)
    const planStartDateCopy = ref(null)
    const lastPlanYear = ref(null)
    const actualPlanYear = ref(null)
    const lastUserActivity = ref(null)
    const actualPlanYearId = ref(null)
    const actualAgencyId = ref(null)
    const functionsBaseUrl = inject('functionsBaseUrl')
    const userData = ref({})
    const allDistinctEmployers = ref([])
    const isLoadingAllDistinctEmployers = ref(false)

    async function createEmployer(employerData) {
      let response = await post(
        'createEmployer',
        `${functionsBaseUrl}/employerGateway/api/v1/employers`,
        employerData
      )
      const created = response.data.data
      if (created?.agency_id) {
        const { markAgencyDataUpdated } = AgencyDataService()
        markAgencyDataUpdated(created.agency_id)
      }
      return created
    }

    function updateTemporaryPhoto(photoURL) {
      userData.value.temporaryPhotoURL = photoURL
    }

    async function getPlanYearsOfEmployer(employerId, agencyId, forceUpdate) {
      if (forceUpdate || !actualPlanYearId.value || planYearsOfActualEmployer.value.length === 0) {
        try {
          const db = getFirestore(getApp())
          const planYearsQuery = query(
            collection(db, 'employer_plan_years'),
            where('employer_id', '==', String(employerId)),
            where('agency_id', '==', String(agencyId))
          )

          const snapshot = await getDocs(planYearsQuery)
          const directPlanYears = snapshot.docs.map(d => ({ ...d.data(), id: d.id }))
          
          planYearsOfActualEmployer.value = directPlanYears.sort((a, b) => new Date(a.plan_start_date) - new Date(b.plan_start_date))
          actualPlanYear.value = find(planYearsOfActualEmployer.value, { id: actualEmployer.value?.planYearId }) || null
          lastPlanYear.value = planYearsOfActualEmployer.value.length > 0 ? planYearsOfActualEmployer.value[planYearsOfActualEmployer.value.length - 1] : null

          if (currentPlanYearCopy.value === null && actualPlanYear.value) {
            currentPlanYearCopy.value = JSON.parse(JSON.stringify(actualPlanYear.value))
          }
          if (planStartDateCopy.value === null && actualPlanYear.value) {
            planStartDateCopy.value = actualPlanYear.value.plan_start_date
          }
          actualPlanYearId.value = actualPlanYear.value?.id

        } catch (error) {
          handleErrors(`getPlanYearsOfEmployer-${employerId}-${agencyId}`, error)
        }
      }
    }

    async function getEmployer(planYearId, forceUpdate = false) {
      if (!planYearId) {
        return null
      }
      if (planYearId !== actualEmployer.value?.planYearId || forceUpdate) {
        const url = `${functionsBaseUrl}/employerGateway/api/v1/plan-years/${planYearId}`
        let response
        try {
          response = await simpleGet('getPlanYearDataById', url)
        } catch (error) {
          handleErrors(`getPlanYearDataById-${planYearId}`, error);
        }
        actualEmployer.value = response?.data?.data || null
        if (!actualEmployer.value) {
          return null
        }
        actualAgencyId.value = actualEmployer.value.agency_id
        const employerId = actualEmployer.value.id
        await getPlanYearsOfEmployer(employerId, actualAgencyId.value, forceUpdate)
      }

      return actualEmployer.value
    }

    async function updateEmployer(employerId, dataEmployer) {
      if (employerId === null) {
        return null
      } else if (employerId === actualEmployer.value?.id) {
        let response = await put(
          'updateEmployer',
          `${functionsBaseUrl}/employerGateway/api/v1/employers/${employerId}`,
          { id: employerId, ...dataEmployer }
        )
        actualEmployer.value = response.data.data
        if (actualAgencyId.value) {
          const { markAgencyDataUpdated } = AgencyDataService()
          markAgencyDataUpdated(actualAgencyId.value)
        }
        return getEmployer(get(actualEmployer.value, 'planYearId'), true)
      }
    }

    function changeLastPlanYear(planYear) {
      currentPlanYearCopy.value = planYear
    }

    function changePlanStartDateCopy(date) {
      planStartDateCopy.value = date
    }

    async function getAllDistinctEmployers(forceRefresh = false) {
      if (!canCallCoreWorkbenchApi()) {
        return []
      }
      if (allDistinctEmployers.value.length > 0 && !forceRefresh) {
        return allDistinctEmployers.value
      }

      isLoadingAllDistinctEmployers.value = true
      try {
        const response = await simpleGet(
          'getAllDistinctEmployers',
          `${functionsBaseUrl}/employerGateway/api/v1/employers?pageSize=0&distinct=true&sortField=name&sortDirection=ASC`
        )
        
        if (response && response.data) {
          allDistinctEmployers.value = response.data.data || []
          return allDistinctEmployers.value
        }
        return []
      } catch (error) {
        handleErrors('Error loading employers URL:', error)
        return []
      } finally {
        isLoadingAllDistinctEmployers.value = false
      }
    }

    async function getLastUserActivity() {
      if (!canCallCoreWorkbenchApi()) {
        return ''
      }
      let response = await simpleGet(
        'getLastUserActivity',
        `${functionsBaseUrl}/loggingGateway/api/v1/logs/users/me/last`)
        if (!response || !response.data) {
          return ''
        }
        lastUserActivity.value = response.data
        return lastUserActivity.value
    }

    function resetAll() {
      actualEmployer.value = null
      planYearsOfActualEmployer.value = null
      currentPlanYearCopy.value = null
      lastPlanYear.value = null
      actualPlanYear.value = null
      actualPlanYearId.value = null
      actualAgencyId.value = null
      planStartDateCopy.value = null
    }

    return {
      actualEmployer,
      planYearsOfActualEmployer,
      currentPlanYearCopy,
      lastPlanYear,
      planStartDateCopy,
      actualPlanYear,
      actualPlanYearId,
      actualAgencyId,
      userData,
      lastUserActivity,
      allDistinctEmployers,
      isLoadingAllDistinctEmployers,
      createEmployer,
      updateTemporaryPhoto,
      getPlanYearsOfEmployer,
      getEmployer,
      updateEmployer,
      changeLastPlanYear,
      changePlanStartDateCopy,
      getLastUserActivity,
      getAllDistinctEmployers,
      resetAll
    }
  },
  {
    persist: true
  }
)
